import {
  Component,
  ElementRef,
  ViewChild,
  OnDestroy,
  input,
  signal,
  HostListener,
  computed,
  inject,
} from '@angular/core';
import { VideoService } from '../../../_services/video-service/video.service';

@Component({
  selector: 'app-video-player',
  standalone: true,
  template: `
    <div
      class="thumbnail-placeholder"
      tabindex="1"
      (keyup)="playVideo()"
      (click)="playVideo()">
      <div
        class="image"
        [style.background-image]="'url(' + imageUrl() + ')'"></div>
      <div class="play-icon">
        <svg
          width="56px"
          height="56px"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          color="#000000"
          stroke-width="1.7">
          <path
            d="M6.90588 4.53682C6.50592 4.2998 6 4.58808 6 5.05299V18.947C6 19.4119 6.50592 19.7002 6.90588 19.4632L18.629 12.5162C19.0211 12.2838 19.0211 11.7162 18.629 11.4838L6.90588 4.53682Z"
            fill="#ffffff"
            stroke="#ffffff"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"></path>
        </svg>
      </div>
    </div>

    @if (active()) {
      <div class="fullscreen-overlay">
        <div class="video-content">
          <div class="row mb-3 align-items-center">
            <div class="col title text-white">
              {{ videoTitle() }}
            </div>
            <div class="col-auto">
              <button
                class="btn-action"
                (click)="closeVideo()">
                <i class="iconoir-xmark"></i>
              </button>
            </div>
          </div>

          <div #videoContainer></div>
        </div>
      </div>
    }
  `,
  styleUrl: '../../../../static/styles/components/video-player.scss',
})
export class VideoPlayerComponent implements OnDestroy {
  private readonly videoService = inject(VideoService);

  @ViewChild('videoContainer') videoContainer?: ElementRef<HTMLDivElement>;
  videoTitle = input.required<string>();
  videoUrl = input.required<string>();
  thumbnailUrl = input<string>();
  imageUrl = computed(() => this.thumbnailUrl() || './assets/video-thumbnails/placeholder-screenshot.png');
  active = signal(false);

  constructor() {
    this.videoService.onPlay$.subscribe((url) => {
      // If a new video starts elsewhere, collapse this component's overlay safely
      if (url !== this.videoUrl() && this.active()) {
        this.active.set(false);
        document.body.style.overflow = 'auto';
      }
    });
  }

  @HostListener('window:keydown.escape')
  onEsc() {
    if (this.active()) this.closeVideo();
  }

  playVideo() {
    this.active.set(true);
    document.body.style.overflow = 'hidden';
    // Wait for Angular to render the @if block and the #videoContainer
    requestAnimationFrame(() => this.initPlayer());
  }

  private initPlayer() {
    if (!this.videoContainer?.nativeElement) {
      requestAnimationFrame(() => this.initPlayer());
      return;
    }

    // Hand over rendering to the service instead of initialising it locally
    this.videoService.startVideo(this.videoUrl(), this.videoContainer.nativeElement);
  }

  closeVideo() {
    // The service decides if it dies, or if it gets rescued to the body for PiP
    this.videoService.handleModalClose();

    this.active.set(false);
    document.body.style.overflow = 'auto';
  }

  ngOnDestroy() {
    if (this.active()) {
      this.closeVideo();
    }
  }
}
